import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

import { getReactionsSummary, getReactionsQueryInfo } from '../utils/data.ts';
import { getBooleanLocationTypes } from '../utils/reactions-utils.ts';
import type {
	ReactionLocation,
	Reaction,
	ReactionsData,
	ReactedUsersMap,
} from '../reactionTypes.ts';

import type {
	useReactionsDataContentQuery$data,
	useReactionsDataContentQuery$variables,
} from './__generated__/useReactionsDataContentQuery.graphql.ts';
import type {
	useReactionsDataCommentsQuery$data,
	useReactionsDataCommentsQuery$variables,
} from './__generated__/useReactionsDataCommentsQuery.graphql.ts';

type ReactionsSummaryForEmoji = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<NonNullable<useReactionsDataContentQuery$data['content']>['nodes']>[number]
		>['contentReactionsSummary']
	>['reactionsSummaryForEmoji'][number]
>;

export const ReactionsContentQuery = graphql`
	query useReactionsDataContentQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				contentReactionsSummary {
					reactionsCount
					ari
					containerAri
					reactionsSummaryForEmoji {
						emojiId
						count
						reacted
						id
					}
				}
			}
		}
	}
`;

export const ReactionsCommentsQuery = graphql`
	query useReactionsDataCommentsQuery($contentId: ID!, $containerId: ID!) {
		comments(commentId: $contentId, contentStatus: [DRAFT, CURRENT]) {
			nodes {
				id
				reactionsSummary(pageId: $containerId, contentType: "page", childType: "comment") {
					reactionsCount
					ari
					containerAri
					reactionsSummaryForEmoji {
						emojiId
						count
						reacted
						id
					}
				}
			}
		}
	}
`;

type ReactionsDataValue = {
	reactions: Reaction[];
	totalCount: number;
	ari: string;
	containerAri: string;
};

type ReactionsDataReturnType = {
	data: ReactionsDataValue;
	loading?: boolean;
	error?: ApolloErrorInterop;
};

type ReactionsDataFnProps = (input: {
	location: ReactionLocation;
	contentId: string;
	containerId: string;
	reactedUsersMap: ReactedUsersMap;
	useNetworkOnly: boolean;
	inheritedReactionsData?: ReactionsData;
	limit?: number;
	sort?: 'asc' | 'desc';
}) => ReactionsDataReturnType;

export const useReactionsData: ReactionsDataFnProps = ({
	location,
	contentId,
	containerId,
	reactedUsersMap,
	useNetworkOnly,
	inheritedReactionsData,
	limit,
	sort,
}) => {
	const { isComment } = getBooleanLocationTypes(location);
	const isLivePage = useIsCurrentPageLive();
	const isClassicEditPage = useIsEditorPage();

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isLivePageSSRMode = isLivePage && Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	const { query, queryVariables } = getReactionsQueryInfo(isComment, contentId, containerId);

	const { data, loading, error } = useQueryInterop<
		useReactionsDataContentQuery$data | useReactionsDataCommentsQuery$data,
		useReactionsDataContentQuery$variables | useReactionsDataCommentsQuery$variables
	>(query, {
		variables: queryVariables,
		fetchPolicy: isComment && useNetworkOnly ? 'network-only' : 'cache-and-network',
		skip: inheritedReactionsData !== undefined, // null is a valid case meaning an error ocurred higher up
	});

	return useMemo(() => {
		// Sometimes we want to show preloaded data during loading instead of empty state
		const showPreloadedDataDuringLoading =
			isLivePageSSRMode || // Always show preloaded data during SSR
			((isLivePage || isClassicEditPage) && fg('confluence_perf_reactions_load_livepage')) || // Show preloaded data for live pages when flag is on
			window.__SSR_REACT_STREAMING__;

		const shouldShowEmptyState =
			(loading && !showPreloadedDataDuringLoading) || // Show empty while loading unless we want to show cached data
			error ||
			(!data && !inheritedReactionsData);

		if (shouldShowEmptyState) {
			return {
				data: {
					reactions: [],
					totalCount: 0,
					ari: '',
					containerAri: '',
				},
				loading,
				error,
			};
		}

		const reactionsSummary = inheritedReactionsData || getReactionsSummary(data);

		const totalCount = reactionsSummary?.reactionsCount || 0;
		const containerAri = reactionsSummary?.containerAri || '';
		const ari = reactionsSummary?.ari || '';
		const reactionNodes = reactionsSummary?.reactionsSummaryForEmoji || [];

		// We need to filter out all the "null" reactions
		const filteredNodes = reactionNodes?.filter(
			(reaction) => reaction !== null,
		) as ReactionsSummaryForEmoji[];

		// Transform reaction nodes to the correct shape for the AkReactions component
		let shapedReactions = filteredNodes.map(({ emojiId, count, reacted }) => {
			const users = reactedUsersMap[emojiId] || [];

			return {
				ari,
				containerAri,
				emojiId,
				count,
				reacted,
				users,
			};
		});

		if (sort) {
			shapedReactions = shapedReactions.sort((a, b) =>
				sort === 'asc' ? a.count - b.count : b.count - a.count,
			);
		}

		if (limit) {
			shapedReactions = shapedReactions.slice(0, limit);
		}

		return {
			data: {
				totalCount,
				reactions: shapedReactions,
				ari,
				containerAri,
			},
		};
	}, [
		data,
		inheritedReactionsData,
		reactedUsersMap,
		loading,
		error,
		sort,
		limit,
		isLivePageSSRMode,
		isLivePage,
		isClassicEditPage,
	]);
};
